import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRealtimeSync } from '../hooks/useRealtimeSync';
import { WebSocketNotification } from '../hooks/useWebSocket';
import { useAuth } from '../hooks/useAuth';
import { formatDistanceToNow } from 'date-fns';

/**
 * 알림 아이콘 반환
 */
const getNotificationIcon = (type: string): string => {
  switch (type) {
    case 'NEW_SAMPLE_REQUEST':
      return '📋';
    case 'SAMPLE_REQUEST_STATUS_CHANGE':
      return '📝';
    case 'SAMPLE_REQUEST_UPDATED':
      return '✏️';
    case 'NEW_FEEDBACK':
      return '💬';
    case 'FEEDBACK_STATUS_CHANGE':
      return '💡';
    case 'NEW_FEEDBACK_REPLY':
      return '💌';
    case 'PROJECT_UPDATE':
      return '📁';
    default:
      return '🔔';
  }
};

/**
 * 알림 타입에 따른 네비게이션 경로 반환
 */
const getNotificationPath = (notification: WebSocketNotification, userType: string | null): string | null => {
  const data = notification.data || {};

  switch (notification.type) {
    case 'NEW_SAMPLE_REQUEST':
    case 'SAMPLE_REQUEST_STATUS_CHANGE':
    case 'SAMPLE_REQUEST_UPDATED':
      if (data.requestId) {
        // projectId 없는 standalone 요청은 공용 라우터(/requests/:id)가 admin/brand 상세를 해소.
        if (!data.projectId) {
          return `/requests/${data.requestId}`;
        }
        return userType === 'admin'
          ? `/admin/management?tab=projects&projectId=${data.projectId}&requestId=${data.requestId}`
          : `/sample-request?tab=projects&project=${data.projectId}&request=${data.requestId}`;
      }
      break;
    case 'NEW_FEEDBACK':
    case 'FEEDBACK_STATUS_CHANGE':
    case 'NEW_FEEDBACK_REPLY':
      if (data.feedbackId) {
        if (userType === 'admin') {
          return `/admin/feedbacks/${data.feedbackId}`;
        } else {
          return `/brand/feedbacks/${data.feedbackId}`;
        }
      }
      break;
    case 'PROJECT_UPDATE':
      if (data.projectId) {
        if (userType === 'admin') {
          return `/admin/management?tab=projects&projectId=${data.projectId}`;
        } else {
          return `/sample-request?tab=projects&project=${data.projectId}`;
        }
      }
      break;
    case 'QUOTATION_UPDATE':
    case 'NEW_QUOTATION':
      if (data.quotationId) {
        if (userType === 'admin') {
          return `/admin/quotations/${data.quotationId}`;
        }
      }
      break;
  }

  return null;
};

interface ToastNotification extends WebSocketNotification {
  id: string;
  dismissed: boolean;
}

/**
 * 알림 컨테이너 컴포넌트
 *
 * 화면 우측 하단에 새 알림을 표시하고, 확인할 때까지 유지됩니다.
 */
const NotificationContainer: React.FC = () => {
  const navigate = useNavigate();
  const { userType } = useAuth();
  const { notifications, markAsRead } = useRealtimeSync();
  const [toasts, setToasts] = useState<ToastNotification[]>([]);
  const [lastNotificationCount, setLastNotificationCount] = useState(0);

  // 새 알림 감지 및 토스트 추가
  useEffect(() => {
    if (notifications.length > lastNotificationCount) {
      // 새로운 알림들만 추가
      const newNotifications = notifications.slice(0, notifications.length - lastNotificationCount);
      const newToasts: ToastNotification[] = newNotifications.map((n, i) => ({
        ...n,
        id: `${Date.now()}-${i}`,
        dismissed: false,
      }));

      setToasts(prev => [...newToasts, ...prev].slice(0, 5)); // 최대 5개까지만 표시
    }
    setLastNotificationCount(notifications.length);
  }, [notifications, lastNotificationCount]);

  // 토스트 닫기
  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // 토스트 클릭 시 이동
  const handleToastClick = useCallback((toast: ToastNotification, index: number) => {
    const path = getNotificationPath(toast, userType);
    markAsRead(index);
    dismissToast(toast.id);
    if (path) {
      navigate(path);
    }
  }, [userType, markAsRead, dismissToast, navigate]);

  if (toasts.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {toasts.map((toast, index) => (
        <div
          key={toast.id}
          className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 animate-slide-in-right cursor-pointer hover:shadow-xl transition-shadow"
          onClick={() => handleToastClick(toast, index)}
        >
          <div className="flex items-start gap-3">
            <span className="text-xl flex-shrink-0">
              {getNotificationIcon(toast.type)}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 line-clamp-2">
                {toast.message}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-xs text-gray-400">
                  {formatDistanceToNow(new Date(toast.timestamp), { addSuffix: true })}
                </p>
                {getNotificationPath(toast, userType) && (
                  <span className="text-xs text-np-600 font-medium">
                    Click to view →
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                dismissToast(toast.id);
              }}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default NotificationContainer;
